import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import OfficerProfile from "./OfficerProfile";
import TeamProfile from "./TeamProfile";

const TEAMS = ["Mechanical Team", "Software Team", "Electrical Team"] as const;
type TeamName = (typeof TEAMS)[number];

const teamData: Record<
  TeamName,
  {
    description: React.ReactNode;
    imagePath: string;
    officers: { imagePath: string; name: string; title: string }[];
  }
> = {
  "Mechanical Team": {
    description: (
      <p>
        The mechanical team is responsible for designing and building all
        structural components of the submarine. We use SolidWorks to create
        detailed 3D models and part drawings for the various components of the
        sub. This software allows us to visualize the entire system, test
        designs virtually, and make necessary adjustments before manufacturing
        the final parts. Once the designs are finalized, we manufacture the
        parts using a variety of materials, including aluminum, acrylic, PVC,
        and PLA. We use 3D printers and the College of Engineering's machine
        shop to produce our parts with a high degree of precision.
        <br />
        <br />
        Our team is involved in ensuring the submarine meets all necessary
        buoyancy and mechanical specifications. We use spreadsheets to perform
        buoyancy calculations, which help us determine the ideal material
        choices and designs for different components. To comply with competition
        specifications, we must ensure that our robot and all components that
        detach from it during competition are buoyant by at least 0.5% by
        weight. One of the most crucial aspects of our work is collaborating
        with the electrical team to determine size requirements for our
        electronics enclosures, ensuring that all components fit securely and
        wires can be routed between boards and our bulkhead connectors. We
        design our electronics enclosures with tight tolerances to ensure that
        they will be waterproof. Additionally, the mechanical team produces the
        items necessary for testing the capabilities of our robot, including
        building structures out of PVC pipe to imitate the competition tasks.
        <br />
        <br />
        Currently, the mechanical team is focused on enhancing the robot's
        capabilities by designing new torpedo and grabber mechanisms. These
        systems are designed to allow our sub to complete more tasks during the
        competition, allowing us to earn more points. We use SolidWorks Flow
        Simulation to perform CFD simulations on our torpedoes to obtain crucial
        information like the drag coefficient. This allows us to determine what
        size of spring we need to propel our torpedoes through the water to
        their target. Creating these systems is a long process that requires us
        to ensure they function properly, while also managing their weights to
        ensure that the sub remains balanced without the excessive addition of
        fishing weights or pool noodles.
      </p>
    ),
    imagePath: "/images/teamPhotos/MechanicalTeamPhoto_smaller.jpg",
    officers: [
      {
        imagePath: "/images/officers/Lauren.jpg",
        name: "Lauren Bouckaert",
        title: "Mechanical Team Lead",
      },
      {
        imagePath: "/images/officers/Izzie.jpg",
        name: "Izzie Mulita",
        title: "Mechanical Team Lead",
      },
    ],
  },
  "Software Team": {
    description: (
      <p>
        The software team's main responsibility is making the sub able to
        navigate an underwater obstacle course autonomously. We seek to
        accomplish this task by using a combination of sensor data and machine
        learning. We use machine learning to help us find objects and choose
        what the agenda is dynamically based on the bot's proximity to tasks.
        The software team also works closely with both the mechanical and
        electrical team members to design and integrate new parts and systems
        into the sub. These additions then allow the bot to tackle new tasks
        such as manipulating shells with a grasper of some kind.
        <br />
        <br />
        As a new bot is built we are taking the opportunity to upgrade and
        update our systems. This update is starting with the switch from legacy
        ROS to its successor ROS 2. While building a new sub we had to come up
        with ways to test the new software so we spent time working to model the
        bot to enable rapid developments while limiting the need for costly pool
        tests.
        <br />
        <br />
        As a team we have worked together to achieve both individual and team
        goals, and are incredibly pleased at the positive impacts made on both
        our members and the school community.
      </p>
    ),
    imagePath: "/images/teamPhotos/SoftwareTeamPhoto_smaller.jpg",
    officers: [
      {
        imagePath: "/images/officers/Andrew.jpg",
        name: "Andrew Culbertson",
        title: "Software Team Lead",
      },
    ],
  },
  "Electrical Team": {
    description: (
      <p>
        This year the surf electrical team is working on a complete redesign of
        the sub's electronics. We are creating several new PCB boards. The
        different boards all serve different functions. First, we are designing
        the circuits in an Electronic Design Automation software (EDA). We
        simulate the circuit in a spice simulator. Second, we go through the
        circuit and start selecting parts and Integrated Circuits (ICs). This
        can be the most time intensive part of the process. We pour over
        datasheets, comparing requirements, and changing our designs to make it
        more practical and efficient. Finally, we build a 3D model of the
        circuit board. This part requires a lot of planning and revision. A lot
        of factors go into a good design. All of these tasks are not taught in
        the Electrical Engineering program and require us to research and learn
        on our own.
        <br />
        <br />
        All of this requires planning. We have created a block diagram this
        year. A block diagram helps organize all the interconnected parts of the
        electronics. We have been working with professors to improve our systems
        and solve problems. This block diagram helped us decide to create four
        circuit boards. We have a power board that connects and regulates the
        battery. A process control board that holds the computer. The sensor
        board that contains important and delicate sensors. And finally, the
        motor board which connects the computer to the motors and controls them.
        <br />
        <br />
        To complete all this, we divide ourselves into smaller groups led by the
        seniors. We try to teach the newer members what we have learned. We
        dedicate a lot of time to documentation. It's important that when the
        senior members leave, we have a well-trained group able to pick up where
        others left off. New members are tasked with assisting seniors with
        their work, doing research projects on circuit design for the sub, and
        making side projects that teach them important skills. The goal of the
        electrical team is that everyone learns as much as possible while
        building the sub as a team.
      </p>
    ),
    imagePath: "/images/teamPhotos/ElectricalTeamPhoto_smaller.jpg",
    officers: [
      {
        imagePath: "/images/officers/Aden.jpg",
        name: "Aden Gianfrancisco",
        title: "Electrical Team Lead",
      },
      {
        imagePath: "/images/officers/Carson_Heurtin.jpg",
        name: "Carson Heurtin",
        title: "Electrical Team Lead",
      },
    ],
  },
};

const TeamsSection = () => {
  const [selectedTeam, setSelectedTeam] = useState<TeamName | null>(null);

  return (
    <div className="teams-section">
      <h3 className="meet-section-title">Meet the Teams:</h3>
      <div className="team-filter-buttons">
        {TEAMS.map((team) => (
          <button
            key={team}
            className={`team-filter-btn${selectedTeam === team ? " team-filter-btn--active" : ""}`}
            aria-pressed={selectedTeam === team}
            onClick={() => setSelectedTeam(selectedTeam === team ? null : team)}
          >
            {team}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {selectedTeam &&
          (() => {
            const data = teamData[selectedTeam];
            return (
              <motion.div
                key={selectedTeam}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.2 }}
              >
                <TeamProfile
                  teamName={selectedTeam}
                  teamDescription={data.description}
                  teamImagePath={data.imagePath}
                  officer1={
                    data.officers[0] && (
                      <OfficerProfile
                        officerImagePath={data.officers[0].imagePath}
                        name={data.officers[0].name}
                        title={data.officers[0].title}
                      />
                    )
                  }
                  officer2={
                    data.officers[1] && (
                      <OfficerProfile
                        officerImagePath={data.officers[1].imagePath}
                        name={data.officers[1].name}
                        title={data.officers[1].title}
                      />
                    )
                  }
                />
              </motion.div>
            );
          })()}
      </AnimatePresence>
    </div>
  );
};

export default TeamsSection;
